/**
 * rulesService.js
 *
 * Lädt das Rules-Objekt von GET /api/v1/rules.
 * Nutzt ETag / If-None-Match für effizientes Caching (spart Bandbreite bei 304).
 * Bei Netzwerkfehler oder HTTP 5xx: Fallback auf rulesFallback.js.
 */

import rulesFallback from '../constants/rulesFallback';
import environments from '../utils/environments';

// In-memory cache — wird beim App-Start einmalig befüllt
let cachedRules = null;
let cachedEtag = null;

const RULES_URL = `${environments.baseUrl}/api/v1/rules`;

/**
 * Lädt die Rules vom Backend.
 * - Sendet If-None-Match wenn ETag vorhanden.
 * - Bei 304: gibt gecachte Rules zurück.
 * - Bei 200: speichert neue Rules + ETag.
 * - Bei Fehler: gibt Fallback zurück (App bleibt funktionsfähig).
 *
 * @returns {Promise<Object>} Rules-Objekt
 */
export async function fetchRules() {
  try {
    const headers = {};
    if (cachedEtag) {
      headers['If-None-Match'] = cachedEtag;
    }

    const response = await fetch(RULES_URL, { headers });

    if (response.status === 304 && cachedRules) {
      // Server bestätigt: gecachte Rules sind noch aktuell
      return cachedRules;
    }

    if (!response.ok) {
      console.warn(`[rulesService] GET /api/v1/rules returned ${response.status} — using fallback`);
      if (!cachedRules) {
        cachedRules = rulesFallback;
      }
      return cachedRules;
    }

    const rules = await response.json();
    const etag = response.headers.get('ETag');

    cachedRules = rules;
    if (etag) {
      cachedEtag = etag;
    }

    return cachedRules;
  } catch (error) {
    console.warn('[rulesService] fetchRules failed — using fallback:', error.message);
    if (!cachedRules) {
      cachedRules = rulesFallback;
    }
    return cachedRules;
  }
}

/**
 * Gibt die gecachten Rules synchron zurück.
 * Gibt rulesFallback zurück wenn noch nicht geladen (nie null).
 *
 * @returns {Object} Rules-Objekt
 */
export function getRulesSync() {
  return cachedRules ?? rulesFallback;
}
